/**
 * Dot path notation parser.
 * @constructor
 * @param {string} pathStr
 * @throws PathParseError
 */
Foundation.Path = function (pathStr) {
  /**
   * @private
   * @type {Foundation.Path.Node}
   */
  this._rootNode = null;

  /**
   * @private
   * @type {Foundation.Path.Node}
   */
  this._lastNode = null;

  // Parse the path string.
  this._scanner = Foundation.Path._sharedScanner();
  this._scanner.setSource(pathStr);
  this._parse();

  // Cleanup.
  this._scanner = null;  // Don't need the scanner anymore.
  this._lastNode = null; // Don't need this reference anymore.
};

/**
 * @private
 * @return {Foundation.Scanner}
 */
Foundation.Path._sharedScanner = function () {
  if (!Foundation.Path._sharedScannerInstance) {
    var scanner = new Foundation.Scanner([
      { "dot":   /\./  },
      { "ident": /\w+/ }
    ]);
    Foundation.Path._sharedScannerInstance = scanner;
  }
  return Foundation.Path._sharedScannerInstance;
};

/**
 * Recursive parsing function.
 * @private
 */
Foundation.Path.prototype._parse = function () {
  var token = this._scanner.nextToken();

  if (token === null) {
    throw new Foundation.Path.Error("Unexpected end of path.", null);
  }

  if (token.type === "ident") {
    this._addNode(new Foundation.Path.Node(token));

    token = this._scanner.nextToken();
    if (token === null) {
      // End of path string, so we're done.
      return;
    }
    if (token.type !== "dot") {
      throw new Foundation.Path.Error("Unexpected token.", token);
    }
  }
  else {
    throw new Foundation.Path.Error("Unexpected token.", token);
  }

  this._parse();
};

/**
 * @private
 * @param {Foundation.Path.Node} node
 */
Foundation.Path.prototype._addNode = function (node) {
  if (!this._rootNode) {
    this._rootNode = node;
    this._lastNode = node;
  }
  else {
    this._lastNode.nextNode = node;
    this._lastNode = node;
  }
};

/**
 * Finds objects in a heirarchy specified by this path. For example,
 * if you had {foo: [{bar: 1}, {bar: 2}]}, running this method
 * with a path of "foo.bar" would result in [1, 2].
 * @public
 * @param {Object} rootObj
 * @return {Array|undefined}
 */
Foundation.Path.prototype.findSpecifiedObjectsIn = function (rootObj) {
  return Foundation.Path._objectsForNodeIn(this._rootNode, rootObj)
};

/**
 * Helper recursive function for findSpefiedObjectsIn method.
 * @private
 * @param {Foundation.Path.Node} node
 * @param {*} obj
 * @return {Array|undefined}
 */
Foundation.Path._objectsForNodeIn = function (node, obj) {
  var result = [];

  // Iterate over array values we come across, and flatten them.
  if (Array.isArray(obj)) {
    var arr = obj;
    for (var i = 0; i < arr.length; i++) {
      var objsFromArray = Foundation.Path._objectsForNodeIn(node, arr[i]);
      if (objsFromArray !== undefined) {
        result = result.concat(objsFromArray);
      }
    }
  }

  // Short circuit on null or undefined values. We want to
  // avoid returning them.
  else if (obj === null || obj === undefined) {
    // Do nothing and fall through to the return.
  }

  // This is the base case. Just return the value if we've
  // reached the end of the linked list.
  else if (node === null) {
    result = [obj];
  }

  // Special handling for properties of Foundation.Observable
  // instances.
  else if (obj instanceof Foundation.Observable) {
    result = Foundation.Path._objectsForNodeIn(node.nextNode, obj.get(node.identifier));
  }

  // And treat everything else like we're just fetching a
  // normal property.
  else {
    result = Foundation.Path._objectsForNodeIn(node.nextNode, obj[node.identifier]);
  }

  // Return undefined if we couldn't find any matching items.
  return result.length ? result : undefined;
};

/**
 * @private
 * @constructor
 * @param {Foundation.Scanner.Token} token
 */
Foundation.Path.Node = function (token) {
  /** @type {string} */
  this.identifier = token.value;

  /** @type {Foundation.Path.Node} */
  this.nextNode = null;
};

/**
 * @public
 * @constructor
 * @param {string} message
 * @param {Foundation.Scanner.Token} token
 * @extends {Error}
 */
Foundation.Path.Error = function (message, token) {
  if (token) message = `${message}(Line:${token.line}, Column: ${token.column})`;
  Error.call(this, message);
  this.name = "PathParseError";
};
Foundation.inherit(Foundation.Path.Error, Error);
